import { ElementRef } from '@angular/core';

/**
 * Invalid form controls, qualified with `.ng-invalid`. Qualifying to real
 * controls (rather than a bare `.ng-invalid`) skips `formGroupName` /
 * `formArrayName` wrapper elements that are only invalid because of a
 * group-level validator, so we scroll to the actual field.
 */
const INVALID_CONTROL_SELECTOR = [
  'input:not([type="hidden"])',
  'textarea',
  'select',
  'mat-select',
  'mat-checkbox',
  'mat-radio-group',
  'mat-slide-toggle',
  '[contenteditable="true"]',
  '[formcontrolname]',
]
  .map((selector) => `${selector}.ng-invalid`)
  .join(', ');

/**
 * Finds the first invalid control inside the host element, smoothly scrolls it
 * into view and moves the cursor into it. The visual error highlight is provided
 * by the existing `ng-invalid`/`ng-touched` design-system styles.
 *
 * Call it after running the form's validation (and flushing change detection) so
 * the invalid state is already rendered.
 *
 * @returns `true` when an invalid field was found and handled.
 */
export function scrollToFirstInvalidControl(host: ElementRef<HTMLElement>): boolean {
  const field = host.nativeElement.querySelector<HTMLElement>(INVALID_CONTROL_SELECTOR);

  if (!field) {
    return false;
  }

  // Scroll the whole Material form-field (label + control + error message) into
  // view when possible, otherwise the control itself.
  (field.closest<HTMLElement>('mat-form-field') ?? field).scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });

  // Move the cursor into the control. Native fields focus directly; composite
  // controls (mat-checkbox, custom components) focus their inner input.
  const focusable = field.matches('input, textarea, select')
    ? field
    : field.querySelector<HTMLElement>('input, textarea, select') ?? field;
  // `preventScroll` keeps focus from interrupting the smooth scroll above.
  focusable.focus({ preventScroll: true });

  return true;
}
